import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { paymentProviderEnum } from "@dfp/bw-isomorphic-enums";

import { useTT } from "../useTT.ts";
import { type BankFlowStep, getBankFlow } from "./bankFlows/index.ts";
import {
  accountForReconnect,
  accountNameLabel,
  generateAccounts,
} from "./demoBankConnect.utils.ts";
import {
  type DemoConnectedAccount,
  savePendingConnection,
} from "./demoConnectionStore.ts";
import { getDemoLanguage } from "./demoLanguage.ts";

// Approximate brand colours for the generic fallback flow's CTA. Hard-coded on
// purpose — these are bank identities, not themeable design tokens.
const BANK_BRAND_COLOR: Record<string, string> = {
  KB: "#A50034",
  CSOB: "#003DA5",
  CSAS: "#2870ED",
  MONETA: "#7A2E8E",
  RAIFFEISEN: "#FEE600",
  FIO: "#009EE0",
  AIRBANK: "#009FE3",
  UNICREDIT: "#E2001A",
  MBANK: "#003D7C",
  CREDITAS: "#00A6A6",
  JTB: "#1A1A1A",
};

/** Third party requesting account access, shown on bank consent screens. */
const THIRD_PARTY_NAME = "Direct Fidoo a.s.";
/** Account owner shown on bank consent screens (the demo company). */
const OWNER_NAME = "Contoso Holdings AG";
/** Username prefilled on bank login screens (demo persona). */
const BANK_LOGIN_USERNAME = "anna.schmidt";

/**
 * Demo-only stand-in for the bank's PSD2 pages. The real flow redirects to the
 * bank; in the demo the mock `initAddBankAccounts` redirects here. Each
 * supported provider renders a faithful replica of its real screens (login →
 * SCA → consent); others fall back to a generic account picker. Picking
 * accounts persists the pending connection and bounces back to the app
 * callback so the existing assignment/authorize steps complete.
 */
export function DemoBankConnectScreen() {
  const { tt } = useTT();
  const { i18n } = useTranslation();

  // This standalone route renders after a full-page reload and does NOT run the
  // user-context bootstrap that re-applies the user's language. Without this,
  // i18next would fall back to the default language and persisted account
  // names would always resolve in English. Re-apply the demo language on mount.
  useEffect(() => {
    const language = getDemoLanguage();
    if (language && language !== i18n.language) {
      void i18n.changeLanguage(language);
    }
  }, [i18n]);

  const params = new URLSearchParams(window.location.search);
  const provider = params.get("provider") ?? "";
  const connectionId = params.get("connectionId") ?? "demo-connection";
  const callback = params.get("callback") ?? "/";
  // The guided tour deep-links straight to the account-consent step so its
  // spotlight target exists without walking the bank login.
  const stepParam = params.get("step");
  const initialStep: BankFlowStep =
    stepParam === "accounts" ? "accounts" : "login";

  const bank = useMemo(
    () => paymentProviderEnum.values.find((item) => item.value === provider),
    [provider],
  );
  const bankName = bank?.name ?? tt("demoBankConnect.fallbackBankName");
  const bic = bank?.bank ?? "";

  // Restore-from-archive / refresh: the bank offers that same account first.
  const reconnectAccountId = params.get("reconnect");

  const accounts = useMemo(
    () => {
      const generated = generateAccounts({
        connectionId,
        bankName,
        bic,
        countryCode: bank?.countryCode,
        getAccountName: (keySuffix) =>
          accountNameLabel(keySuffix, i18n.language),
      });
      const restored = reconnectAccountId
        ? accountForReconnect(reconnectAccountId, connectionId)
        : undefined;
      // Reconnect / restore: the bank re-shares just that account.
      return restored ? [restored] : generated;
    },
    // `i18n.language` re-runs the memo so account names re-translate when the
    // persisted language is applied on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [connectionId, bankName, bic, bank?.countryCode, reconnectAccountId, i18n.language, tt],
  );

  const handleConnect = (selected: DemoConnectedAccount[]) => {
    savePendingConnection({
      connectionId,
      paymentProvider: provider,
      bankName,
      bic,
      accounts: selected,
    });

    // Bounce back to the app callback, adding the connection id the return
    // handler (useConnectBankAccountAssignmentTrigger) reads.
    const url = new URL(callback, window.location.origin);
    url.searchParams.set("connection_id", connectionId);
    window.location.href = url.toString();
  };

  const handleCancel = () => {
    // Declined on the bank page — return to the app without a connection id.
    window.location.href = new URL(callback, window.location.origin).toString();
  };

  const BankFlow = getBankFlow(provider);

  return (
    <BankFlow
      bankName={bankName}
      bic={bic}
      brandColor={BANK_BRAND_COLOR[provider] ?? "#0B6E4F"}
      thirdPartyName={THIRD_PARTY_NAME}
      ownerName={OWNER_NAME}
      username={BANK_LOGIN_USERNAME}
      connectionId={connectionId}
      accounts={accounts}
      initialStep={initialStep}
      onConnect={handleConnect}
      onCancel={handleCancel}
    />
  );
}
